import React from 'react';
import { Badge, Text, VStack, Tooltip, HStack } from '@chakra-ui/react';
import StageBadge from './StageBadge';
import TagsCell from './TagsCell';
import moment from 'moment';
import { DEAL_PARTY_ROLES } from '../../constants/crmConstants';

// 'buyer-agent' → configured label ('Buyer's Agent'); unknown slugs title-case
const dealTeamRoleLabel = (slug) => {
  const cfg = DEAL_PARTY_ROLES.find((r) => r.value === slug);
  if (cfg) return cfg.label;
  return String(slug || '')
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
};

export const ALL_COLUMNS = [
  {
    id: 'name',
    label: 'Name',
    accessor: (deal) => deal.dealName || '',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '200px',
    isDefault: true,
    renderCell: (deal, onClick) => (
      <Text fontWeight="medium" cursor="pointer" onClick={() => onClick(deal)}>
        {deal.dealName}
      </Text>
    ),
  },
  {
    id: 'stage',
    label: 'Stage',
    accessor: (deal) => deal.stage,
    sortable: true,
    filterable: true,
    filterType: 'multiselect',
    filterOptions: [], // populated dynamically from CrmSettingsContext
    width: '140px',
    isDefault: true,
    renderCell: (deal) => <StageBadge stage={deal.stage} />,
  },
  {
    id: 'source',
    label: 'Source',
    accessor: (deal) => deal.source || '',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '120px',
    isDefault: true,
    renderCell: (deal) => <Text fontSize="sm" color="gray.600" textTransform="capitalize">{deal.source}</Text>,
  },
  {
    id: 'product',
    label: 'Product',
    accessor: (deal) => deal.product || '',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '120px',
    isDefault: false,
    renderCell: (deal) => <Text fontSize="sm" textTransform="capitalize">{deal.product || '—'}</Text>,
  },
  {
    id: 'amount',
    label: 'Amount',
    accessor: (deal) => deal.amount || 0,
    sortable: true,
    filterable: false,
    width: '120px',
    isDefault: false,
    renderCell: (deal) => (
      <Text fontSize="sm" fontWeight="semibold">
        {deal.amount > 0 ? `$${deal.amount.toLocaleString()}` : '—'}
      </Text>
    ),
  },
  {
    id: 'phone',
    label: 'Phone',
    accessor: (deal) => deal.contact?.phone || '',
    sortable: false,
    filterable: true,
    filterType: 'text',
    width: '130px',
    isDefault: true,
    renderCell: (deal) => <Text fontSize="sm">{deal.contact?.phone || '—'}</Text>,
  },
  {
    id: 'email',
    label: 'Email',
    accessor: (deal) => deal.contact?.email || '',
    sortable: false,
    filterable: true,
    filterType: 'text',
    width: '200px',
    isDefault: false,
    renderCell: (deal) => (
      <Text fontSize="sm" noOfLines={1}>
        {deal.contact?.email || '—'}
      </Text>
    ),
  },
  {
    id: 'lastTouched',
    label: 'Last Touched',
    accessor: (deal) => {
      const timestamp = deal.updatedAt || deal.createdAt;
      return timestamp?.seconds || 0;
    },
    sortable: true,
    filterable: false,
    width: '130px',
    isDefault: true,
    renderCell: (deal) => {
      const timestamp = deal.updatedAt || deal.createdAt;
      return (
        <Text fontSize="sm" color="gray.600">
          {timestamp
            ? moment(timestamp.seconds ? timestamp.seconds * 1000 : timestamp).fromNow()
            : '—'}
        </Text>
      );
    },
  },
  {
    id: 'nextAction',
    label: 'Next Action',
    accessor: (deal) => deal.nextAction || '',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '140px',
    isDefault: false,
    renderCell: (deal) => <Text fontSize="sm">{deal.nextAction || '—'}</Text>,
  },
  {
    id: 'due',
    label: 'Due',
    accessor: (deal) => {
      // Use earliest task due date if available, otherwise fall back to nextActionDue
      const dueDate = deal.earliestTaskDue || deal.nextActionDue;
      if (!dueDate) return 0;
      return dueDate.seconds || new Date(dueDate).getTime() / 1000;
    },
    sortable: true,
    filterable: false,
    width: '110px',
    isDefault: false,
    renderCell: (deal) => {
      // Use earliest task due date if available, otherwise fall back to nextActionDue
      const dueDate = deal.earliestTaskDue || deal.nextActionDue;
      const hasOverdueTask = deal.hasOverdueTask;
      const taskCount = deal.activeTaskCount || 0;
      
      const isOverdue = hasOverdueTask || (dueDate &&
        moment(dueDate.seconds ? dueDate.seconds * 1000 : dueDate)
          .isBefore(moment(), 'day'));
      
      return (
        <VStack spacing={0} align="flex-start">
          <Text fontSize="sm" color={isOverdue ? 'red.500' : 'gray.600'}>
            {dueDate
              ? moment(
                  dueDate.seconds
                    ? dueDate.seconds * 1000
                    : dueDate
                ).format('MM/DD/YY')
              : '—'}
          </Text>
          {taskCount > 0 && (
            <Text fontSize="xs" color={isOverdue ? 'red.400' : 'gray.400'}>
              {taskCount} task{taskCount > 1 ? 's' : ''}
            </Text>
          )}
        </VStack>
      );
    },
  },
  {
    id: 'created',
    label: 'Created',
    accessor: (deal) => deal.createdAt?.seconds || 0,
    sortable: true,
    filterable: false,
    width: '110px',
    isDefault: true,
    renderCell: (deal) => (
      <Text fontSize="sm" color="gray.600">
        {deal.createdAt
          ? moment(deal.createdAt.seconds ? deal.createdAt.seconds * 1000 : deal.createdAt).format('MM/DD/YY')
          : '—'}
      </Text>
    ),
  },
  {
    id: 'tags',
    label: 'Tags',
    accessor: (deal) => (deal.tags || []).join(', '),
    sortable: false,
    filterable: true,
    filterType: 'multiselect',
    filterOptions: [], // Dynamic - populated at runtime from CRM settings + deal data
    width: '150px',
    isDefault: true,
    renderCell: (deal) => <TagsCell tags={deal.tags} />,
  },
  {
    id: 'dealTeamRoles',
    label: 'Deal Team',
    accessor: (deal) => (deal.dealTeamRoles || []).join(', '),
    sortable: false,
    filterable: true,
    filterType: 'multiselect',
    filterOptions: [], // Dynamic — populated from CRM settings (dealPartyRoles)
    width: '170px',
    isDefault: false,
    renderCell: (deal) => {
      const roles = deal.dealTeamRoles || [];
      if (roles.length === 0) return <Text fontSize="xs" color="gray.400">—</Text>;
      const shown = roles.slice(0, 2);
      return (
        <HStack spacing={1} flexWrap="wrap">
          {shown.map((r) => (
            <Badge key={r} colorScheme="purple" variant="subtle" fontSize="2xs">
              {dealTeamRoleLabel(r)}
            </Badge>
          ))}
          {roles.length > 2 && (
            <Tooltip label={roles.slice(2).map(dealTeamRoleLabel).join(', ')}>
              <Badge colorScheme="gray" variant="subtle" fontSize="2xs">+{roles.length - 2}</Badge>
            </Tooltip>
          )}
        </HStack>
      );
    },
  },
  {
    id: 'propertyAddress',
    label: 'Property Address',
    accessor: (deal) => deal.propertyAddress?.full || '',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '250px',
    isDefault: false,
    renderCell: (deal) => (
      <Text fontSize="sm" noOfLines={1}>
        {deal.propertyAddress?.full || '—'}
      </Text>
    ),
  },
  {
    id: 'propertyType',
    label: 'Property Type',
    accessor: (deal) => deal.propertyType || '',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '130px',
    isDefault: false,
    renderCell: (deal) => <Text fontSize="sm">{deal.propertyType || '—'}</Text>,
  },
  {
    id: 'purchaseOrRefi',
    label: 'Purchase/Refi',
    accessor: (deal) => deal.purchaseOrRefi || '',
    sortable: true,
    filterable: true,
    filterType: 'multiselect',
    filterOptions: [
      { value: 'Purchase', label: 'Purchase' },
      { value: 'Refinance', label: 'Refinance' },
    ],
    width: '130px',
    isDefault: false,
    renderCell: (deal) => <Text fontSize="sm">{deal.purchaseOrRefi || '—'}</Text>,
  },
  {
    id: 'loanPurpose',
    label: 'Loan Purpose',
    accessor: (deal) => deal.loanPurpose || '',
    sortable: true,
    filterable: true,
    filterType: 'text',
    width: '150px',
    isDefault: true,
    renderCell: (deal) => <Text fontSize="sm">{deal.loanPurpose || '—'}</Text>,
  },
  {
    id: 'downPayment',
    label: 'Down Payment',
    accessor: (deal) => deal.downPayment || '',
    sortable: true,
    filterable: false,
    width: '120px',
    isDefault: false,
    renderCell: (deal) => (
      <Text fontSize="sm">{deal.downPayment ? `${deal.downPayment}%` : '—'}</Text>
    ),
  },
  {
    id: 'creditScore',
    label: 'Credit Score',
    accessor: (deal) => deal.creditScore || '',
    sortable: true,
    filterable: false,
    width: '110px',
    isDefault: false,
    renderCell: (deal) => <Text fontSize="sm">{deal.creditScore || '—'}</Text>,
  },
  {
    id: 'appDate',
    label: 'App Date',
    accessor: (deal) => {
      if (!deal.appDate) return 0;
      return deal.appDate.seconds || new Date(deal.appDate).getTime() / 1000;
    },
    sortable: true,
    filterable: false,
    width: '110px',
    isDefault: false,
    renderCell: (deal) => (
      <Text fontSize="sm">
        {deal.appDate
          ? moment(deal.appDate.toDate ? deal.appDate.toDate() : deal.appDate).format('MM/DD/YY')
          : '—'}
      </Text>
    ),
  },
  {
    id: 'estClosingDate',
    label: 'Est. Closing',
    accessor: (deal) => {
      const date = deal.estimatedClosingDate;
      if (!date) return 0;
      return date.seconds || new Date(date).getTime() / 1000;
    },
    sortable: true,
    filterable: false,
    width: '110px',
    isDefault: false,
    renderCell: (deal) => {
      const date = deal.estimatedClosingDate;
      return (
        <Text fontSize="sm">
          {date
            ? moment(date.toDate ? date.toDate() : date).format('MM/DD/YY')
            : '—'}
        </Text>
      );
    },
  },
  {
    id: 'buyerAgent',
    label: 'Buyer Agent',
    accessor: (deal) => {
      // Check relatedParties first for buyer-agent role
      const buyerAgentParty = deal.relatedParties?.find(p => p.role === 'buyer-agent');
      if (buyerAgentParty) return buyerAgentParty.contactName || '';
      // Fall back to legacy buyerAgent field
      return deal.buyerAgent?.name || '';
    },
    sortable: false,
    filterable: true,
    filterType: 'text',
    width: '150px',
    isDefault: false,
    renderCell: (deal) => {
      // Check relatedParties first for buyer-agent role
      const buyerAgentParty = deal.relatedParties?.find(p => p.role === 'buyer-agent');
      const name = buyerAgentParty?.contactName || deal.buyerAgent?.name || '—';
      const phone = buyerAgentParty?.contactPhone || deal.buyerAgent?.phone || '';
      
      return (
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="sm">{name}</Text>
          {phone && (
            <Text fontSize="xs" color="gray.500">
              {phone}
            </Text>
          )}
        </VStack>
      );
    },
  },
  {
    id: 'sellerAgent',
    label: 'Seller Agent',
    accessor: (deal) => {
      // Check relatedParties first for listing-agent or seller-agent role
      const sellerAgentParty = deal.relatedParties?.find(p => p.role === 'listing-agent' || p.role === 'seller-agent');
      if (sellerAgentParty) return sellerAgentParty.contactName || '';
      // Fall back to legacy sellerAgent field
      return deal.sellerAgent?.name || '';
    },
    sortable: false,
    filterable: true,
    filterType: 'text',
    width: '150px',
    isDefault: false,
    renderCell: (deal) => {
      // Check relatedParties first for listing-agent or seller-agent role
      const sellerAgentParty = deal.relatedParties?.find(p => p.role === 'listing-agent' || p.role === 'seller-agent');
      const name = sellerAgentParty?.contactName || deal.sellerAgent?.name || '—';
      const phone = sellerAgentParty?.contactPhone || deal.sellerAgent?.phone || '';
      
      return (
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="sm">{name}</Text>
          {phone && (
            <Text fontSize="xs" color="gray.500">
              {phone}
            </Text>
          )}
        </VStack>
      );
    },
  },
  {
    id: 'slaTime',
    label: 'SLA Time',
    accessor: (deal) => {
      const start = deal.slaTimestamp || deal.createdAt;
      if (!start) return 0;
      const startTime = start.seconds ? start.seconds : new Date(start).getTime() / 1000;
      return Date.now() / 1000 - startTime;
    },
    sortable: true,
    filterable: false,
    width: '100px',
    isDefault: false,
    renderCell: (deal) => {
      const start = deal.slaTimestamp || deal.createdAt;
      if (!start) return <Text fontSize="sm">—</Text>;
      
      const startTime = start.seconds ? moment(start.seconds * 1000) : moment(start);
      const duration = moment.duration(moment().diff(startTime));
      
      let slaText = '';
      if (duration.asMinutes() < 60) slaText = `${Math.floor(duration.asMinutes())}m`;
      else if (duration.asHours() < 24) slaText = `${Math.floor(duration.asHours())}h`;
      else slaText = `${Math.floor(duration.asDays())}d`;
      
      return <Text fontSize="sm">{slaText}</Text>;
    },
  },
  {
    id: 'atRisk',
    label: 'At Risk',
    accessor: (deal) => deal.isAtRisk ? 'Yes' : 'No',
    sortable: true,
    filterable: true,
    filterType: 'multiselect',
    filterOptions: [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' },
    ],
    width: '100px',
    isDefault: false,
    renderCell: (deal) => (
      <Badge colorScheme={deal.isAtRisk ? 'red' : 'green'} fontSize="xs">
        {deal.isAtRisk ? 'Yes' : 'No'}
      </Badge>
    ),
  },
  {
    id: 'middleInitial',
    label: 'MI',
    accessor: (deal) => deal.contact?.middleInitial || deal.middleInitial || '',
    sortable: false,
    filterable: false,
    width: '60px',
    isDefault: false,
    renderCell: (deal) => (
      <Text fontSize="sm">{deal.contact?.middleInitial || deal.middleInitial || '—'}</Text>
    ),
  },
  {
    id: 'owner',
    label: 'Owner',
    accessor: (deal) => deal.ownerId || '',
    sortable: true,
    filterable: false,
    width: '150px',
    isDefault: false,
    renderCell: (deal, onClick, usersMap) => {
      const ownerName = usersMap?.get(deal.ownerId) || 'Unassigned';
      return <Text fontSize="sm">{ownerName}</Text>;
    },
  },
  {
    id: 'recordScope',
    label: 'Scope',
    // Record scope is an admin-only concept: LOs seeing "company record"
    // assume the company is claiming their book. Hidden from non-admin
    // pickers, table renders, and exports.
    adminOnly: true,
    accessor: (deal) => deal.recordScope || 'user',
    sortable: true,
    filterable: true,
    filterType: 'select',
    width: '110px',
    isDefault: false,
    renderCell: (deal) =>
      deal.recordScope === 'company' ? (
        <Badge colorScheme="purple" fontSize="xs">Company</Badge>
      ) : (
        <Text fontSize="sm" color="gray.400">—</Text>
      ),
  },
];

// Default columns in specific display order
export const DEFAULT_VISIBLE_COLUMNS = [
  'name',
  'stage',
  'tags',
  'created',
  'phone',
  'source',
  'lastTouched',
  'loanPurpose',
];

export const DEFAULT_PREFERENCES = {
  viewMode: 'table',
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
  columnOrder: DEFAULT_VISIBLE_COLUMNS,
  sortBy: 'created',
  sortDirection: 'desc',
  filters: {},
};

export const getColumnById = (columnId) => {
  return ALL_COLUMNS.find(col => col.id === columnId);
};

export const getVisibleColumns = (visibleColumnIds) => {
  return visibleColumnIds
    .map(id => ALL_COLUMNS.find(col => col.id === id))
    .filter(Boolean);
};

/**
 * Field definitions for FilterBuilder component
 * Maps column definitions to filter-compatible format
 */
export const DEAL_FILTER_FIELDS = [
  { id: 'dealName', label: 'Deal Name', type: 'text', dbField: 'deal_name' },
  { id: 'stage', label: 'Stage', type: 'select', dbField: 'stage', options: [] }, // populated dynamically from CrmSettingsContext
  { id: 'amount', label: 'Amount', type: 'number', dbField: 'amount' },
  { id: 'source', label: 'Source', type: 'text', dbField: 'source_medium' },
  { id: 'product', label: 'Product', type: 'text', dbField: 'product' },
  { id: 'loanNumber', label: 'Loan Number', type: 'text', dbField: 'loan_number' },
  { id: 'lender', label: 'Lender', type: 'text', dbField: 'lender' },
  { id: 'propertyState', label: 'Property State', type: 'text', dbField: 'property_state' },
  { id: 'propertyCity', label: 'Property City', type: 'text', dbField: 'property_city' },
  { id: 'propertyType', label: 'Property Type', type: 'text', dbField: 'property_type' },
  { id: 'occupancyType', label: 'Occupancy Type', type: 'select', dbField: 'occupancy_type', options: [
    { value: 'primary_residence', label: 'Primary Residence' },
    { value: 'second_home', label: 'Second Home' },
    { value: 'investment', label: 'Investment Property' },
  ]},
  { id: 'loanPurpose', label: 'Loan Purpose', type: 'select', dbField: 'loan_purpose', options: [
    { value: 'Purchase', label: 'Purchase' },
    { value: 'Rate/Term Refinance', label: 'Rate/Term Refinance' },
    { value: 'Cash Out', label: 'Cash Out' },
  ]},
  { id: 'tags', label: 'Tags', type: 'array', dbField: 'tags', options: [] }, // populated dynamically from CrmSettingsContext
  { id: 'dealTeamRoles', label: 'Deal Team Role', type: 'array', dbField: 'deal_team_roles', options: [] }, // populated dynamically from CrmSettingsContext (dealPartyRoles)
  { id: 'activeCampaigns', label: 'Enrolled in Campaign', type: 'array', dbField: 'active_campaign_ids', options: [] }, // Options from useCampaignFilterOptions
  { id: 'isAtRisk', label: 'At Risk', type: 'boolean', dbField: 'is_at_risk' },
  { id: 'dnc', label: 'DNC: Everything', type: 'boolean', dbField: 'dnc' },
  { id: 'dncCall', label: 'DNC: Call', type: 'boolean', dbField: 'dnc_call' },
  { id: 'dncEmail', label: 'DNC: Email', type: 'boolean', dbField: 'dnc_email' },
  { id: 'dncSms', label: 'DNC: SMS', type: 'boolean', dbField: 'dnc_sms' },
  { id: 'creditScore', label: 'Credit Score', type: 'number', dbField: 'credit_score' },
  { id: 'downPayment', label: 'Down Payment %', type: 'number', dbField: 'down_payment' },
  { id: 'createdAt', label: 'Created Date', type: 'date', dbField: 'created_at' },
  { id: 'estimatedClosingDate', label: 'Est. Closing Date', type: 'date', dbField: 'estimated_closing_date' },
  { id: 'nextActionDue', label: 'Next Action Due', type: 'date', dbField: 'next_action_due' },
  { id: 'ownerId', label: 'Owner', type: 'text', dbField: 'owner_id' },
  { id: 'recordScope', label: 'Record Scope', type: 'select', dbField: 'record_scope', options: [
    { value: 'company', label: 'Company' },
    { value: 'user', label: 'Personal' },
  ] },
];

